import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { useState } from 'react';
import styled from 'styled-components';

import { UNARY_OPERATORS } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/constants';
import {
  generateOperatorOptions,
  getRightHandDisplay,
} from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import { LOGIC_RULE_INPUT_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/FieldOrValueInput';
import FieldValueBlock from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/FieldValueBlock';
import { buildRule } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import CaseDataManagerSchemaSelect from 'dashboard/components/CaseDataManagerSchemaSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import {
  ConditionalRule,
  getConditionalRuleParts,
  JsonLogicValue,
  RuleValueConfigField,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Button from 'design-system/components/Button';
import { FieldSchemaType } from 'lib/fields';
import { isJsonSchemaObject, JsonSchema } from 'lib/json-schema';
import { isMultiValue } from 'lib/workflows';

const DEFAULT_LEFT_CONFIG: RuleValueConfigField = {
  type: RuleValueTypes.Field,
  value: { var: '' },
  paths: [''],
};

const getDefaultLeftConfig = (jsonSchema: JsonSchema): RuleValueConfigField => {
  if (
    isJsonSchemaObject(jsonSchema) &&
    Object.keys(jsonSchema.properties).length === 1 &&
    !isJsonSchemaObject(Object.values(jsonSchema.properties)[0])
  ) {
    // populate LH value by default if json schema only contains a single non-object value
    return produce(DEFAULT_LEFT_CONFIG, (draftConfig) => {
      draftConfig.value.var = Object.keys(jsonSchema.properties)[0];
    });
  } else {
    return DEFAULT_LEFT_CONFIG;
  }
};

const generateNewOperatorOptions = ({
  leftValue,
  jsonSchema,
  operatorsMap,
  operator,
  schemaForFieldPath,
}: {
  leftValue: string;
  jsonSchema: JsonSchema;
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;
  operator: WorkflowOperatorKey | undefined;
  schemaForFieldPath: ReturnType<typeof useSchemaForFieldPath>;
}): Array<{ label: string; value: WorkflowOperatorKey }> => {
  if (leftValue == null) return [];
  const rowSchema: JsonSchema | null = schemaForFieldPath(leftValue, jsonSchema).schema;
  return generateOperatorOptions(rowSchema, operatorsMap, operator);
};

const EditBaseRule = styled.div<{
  editable: boolean;
}>`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  margin-top: ${Spacing[1]};
  margin-bottom: ${Spacing[1]};

  ${({ editable }) => editable && `grid-template-columns: 30% 18% 42% 8%`}
  ${({ editable }) => !editable && `grid-template-columns: 39% 14% 39%`}
`;

interface EditRuleProps {
  baseRule: ConditionalRule;
  jsonSchema: JsonSchema;
  operatorsMap: Partial<Record<FieldSchemaType, WorkflowOperatorKey[]>>;
  editable: boolean;
  enableValidation: boolean;
  isValid: boolean;
  deleteRule: () => void;
  updateRule: (rule: ConditionalRule) => void;
}

const EditRule = ({
  baseRule,
  jsonSchema,
  operatorsMap,
  editable,
  enableValidation,
  isValid,
  deleteRule,
  updateRule,
}: EditRuleProps) => {
  const schemaForFieldPath = useSchemaForFieldPath();

  const { operator, valueLeftConfig, valueRightConfig } = getConditionalRuleParts(baseRule);

  // By defaulting to Value, if the field/value dropdown is not enabled,
  // only the value dropdown is available
  const initialDropdownType = valueRightConfig ? valueRightConfig.type : RuleValueTypes.Value;
  const [dropdownType, setDropdownType] = useState(initialDropdownType);
  const leftConfig = valueLeftConfig || getDefaultLeftConfig(jsonSchema);
  const leftValue = leftConfig.value.var;
  const rightValue = getRightHandDisplay(valueRightConfig);

  const [operatorOptions, setOperatorOptions] = useState(
    generateNewOperatorOptions({
      leftValue,
      jsonSchema,
      operatorsMap,
      operator,
      schemaForFieldPath,
    }),
  );

  const valid = !enableValidation || isValid;

  const isOperatorDropdownEnabled =
    editable && operatorOptions?.length > 0 && leftValue?.length > 0;

  const updateRuleWithLeftValue = (val: JsonLogicValue) => {
    if (!val || val === '') {
      // Empty means we have cleared out the rule
      deleteRule();
    }

    const newOperatorOptions = generateNewOperatorOptions({
      leftValue: val?.toString() ?? '',
      jsonSchema,
      operatorsMap,
      operator: undefined,
      schemaForFieldPath,
    });

    const ruleOperator =
      operator != null && newOperatorOptions.map((option) => option.value).includes(operator)
        ? operator
        : newOperatorOptions?.[0]?.value;

    setOperatorOptions(newOperatorOptions);
    if (ruleOperator != null) {
      updateRule(buildRule(ruleOperator, val, dropdownType));
    } else {
      // If there is no operator given this change, also assume that we intend
      // to clear the rule
      deleteRule();
    }
  };

  const updateRuleWithOperator = (op: WorkflowOperatorKey) => {
    const isUnaryOperator = UNARY_OPERATORS.includes(op);
    const nonMatchingOperators = isMultiValue(operator) !== isMultiValue(op);
    const updatedRuleRightValue = isUnaryOperator || nonMatchingOperators ? undefined : rightValue;
    updateRule(buildRule(op, leftValue, dropdownType, updatedRuleRightValue));
  };

  const updateRuleWithRightValue = (val: JsonLogicValue) =>
    updateRule(buildRule(operator, leftValue, dropdownType, val ?? ''));

  return (
    <EditBaseRule editable={editable}>
      <CaseDataManagerSchemaSelect
        id={`${LOGIC_RULE_INPUT_ID}__left-field`}
        inputValue={leftValue}
        jsonSchema={jsonSchema}
        disabled={!editable}
        hasError={!valid && !leftValue}
        onValueChange={updateRuleWithLeftValue}
        portal
      />
      <ReactSelect
        id={`${LOGIC_RULE_INPUT_ID}__operator`}
        aria-label='Select operator'
        value={operator}
        options={operatorOptions ?? []}
        disabled={!isOperatorDropdownEnabled}
        hasError={isOperatorDropdownEnabled && operator != null && !valid}
        onChange={updateRuleWithOperator}
        portal
      />
      {!UNARY_OPERATORS.includes(operator) && leftValue != null && (
        <FieldValueBlock
          formattedRightValue={rightValue}
          operator={operator}
          dropdownType={dropdownType}
          jsonSchema={jsonSchema}
          fieldSchema={schemaForFieldPath(leftValue, jsonSchema).schema}
          editable={editable}
          hasOperatorOptions={operatorOptions.length > 0}
          valid={valid}
          setDropdownType={setDropdownType}
          updateRuleWithRightValue={updateRuleWithRightValue}
        />
      )}
      {editable && (
        <Button
          variant='basic'
          width='icon'
          aria-label='Delete rule'
          id='multi-input__value-remove'
          onClick={deleteRule}
        >
          <TrashIcon />
        </Button>
      )}
    </EditBaseRule>
  );
};

export default EditRule;
